// Global Instructions Rule Applied!
//
// Mobile-only Redux slice holding the ordered "recently viewed" list. Hydrated from
// localStorage at module init so Home shows recents immediately on a cold launch; the
// write-back is wired once in MobileShell (a `store.subscribe` persister) so this slice
// stays presentation-layer and the shared store middleware chain is untouched.

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RecentItem, loadRecents, RECENTS_CAP } from './recentsStorage.ts'

export interface MobileRecentsState {
  items: RecentItem[]
}

const initialState: MobileRecentsState = {
  items: loadRecents()
}

interface RecordViewPayload {
  id: string
  module: string
  title: string
  subtitle?: string
}

const mobileRecentsSlice = createSlice({
  name: 'mobileRecents',
  initialState,
  reducers: {
    /**
     * Record (or re-surface) a viewed record. Dedupes by `module:id` and unshifts to the
     * front, so re-opening a record moves it to the top; the list is capped at RECENTS_CAP.
     */
    recordView: (state, action: PayloadAction<RecordViewPayload>) => {
      const { id, module, title, subtitle } = action.payload
      const key = `${module}:${id}`
      const next = state.items.filter((it) => `${it.module}:${it.id}` !== key)
      next.unshift({ id, module, title, subtitle, ts: Date.now() })
      state.items = next.slice(0, RECENTS_CAP)
    },
    clearRecents: (state) => {
      state.items = []
    }
  }
})

export const { recordView, clearRecents } = mobileRecentsSlice.actions

/** Most-recent-first list of viewed records. */
export const selectRecents = (state: any): RecentItem[] => state?.mobileRecents?.items ?? []

export default mobileRecentsSlice.reducer
